import { SITES } from '../../../../utils/constants';
import { normalizeAvailableRoles } from '../../../../utils/registrationRoles';
import { getAnnouncementLogo } from '../../../../utils/announcementLogo';

const PREVIEW_READY_EVENT = 'cc-announcement-preview-ready';
const PREVIEW_PAYLOAD_EVENT = 'cc-announcement-preview-payload';
const PREVIEW_TIMEOUT_MS = 15000;

const resolvePlatformBaseUrl = () => {
  if (typeof window === 'undefined') {
    return SITES.LOCAL.platform;
  }

  const currentOrigin = window.location.origin;

  if (currentOrigin === SITES.PROD.admin) {
    return SITES.PROD.platform;
  }

  if (currentOrigin === SITES.DEV.admin.replace(/\/$/, '')) {
    return SITES.DEV.platform;
  }

  return SITES.LOCAL.platform;
};

const buildPreviewUrl = (previewType, previewId) => {
  const baseUrl = resolvePlatformBaseUrl();
  const normalizedBaseUrl = baseUrl.replace(/\/$/, '');
  return `${normalizedBaseUrl}/preview/convocatoria/${previewType}?previewId=${encodeURIComponent(
    previewId
  )}`;
};

const sanitizeImageValue = (value) => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object' && typeof value.data === 'string') {
    return {
      name: value.name || '',
      data: value.data,
    };
  }

  return '';
};

export const buildPreviewPayload = (previewState = {}) => {
  const platform = {
    ...previewState.configuration?.platform,
    ...previewState.platform,
  };
  const logo = getAnnouncementLogo(
    { ...previewState.landing, configuration: { platform } },
    previewState.client
  );

  return {
    id: previewState.id || null,
    status: previewState.status || null,
    client: {
      id: previewState.client?.id || null,
      name: previewState.client?.name || '',
      logo: sanitizeImageValue(previewState.client?.logo),
    },
    configuration: {
      ...(previewState.configuration || {}),
      platform,
      registration: {
        ...(previewState.configuration?.registration || {}),
        availableRoles: normalizeAvailableRoles(
          previewState.configuration?.registration?.availableRoles
        ),
      },
    },
    landing: {
      ...previewState.landing,
      logo,
    },
    login: {
      ...previewState.login,
    },
    platform: {
      ...platform,
      logo,
      platformBanner: sanitizeImageValue(platform.platformBanner),
    },
  };
};

const openAnnouncementWebPreview = ({ previewType, previewState }) => {
  if (typeof window === 'undefined') {
    return;
  }

  const previewId = `preview-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
  const previewUrl = buildPreviewUrl(previewType, previewId);
  const targetOrigin = new URL(resolvePlatformBaseUrl()).origin;
  const payload = buildPreviewPayload(previewState);
  const previewWindow = window.open(previewUrl, '_blank');

  if (!previewWindow) {
    return;
  }

  let timeoutId = null;

  const cleanup = () => {
    window.removeEventListener('message', handleReadyMessage);
    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }
  };

  const handleReadyMessage = (event) => {
    if (event.origin !== targetOrigin) {
      return;
    }

    const message = event.data || {};
    if (
      message?.type !== PREVIEW_READY_EVENT ||
      message?.previewId !== previewId
    ) {
      return;
    }

    previewWindow.postMessage(
      {
        type: PREVIEW_PAYLOAD_EVENT,
        previewId,
        payload,
      },
      targetOrigin
    );
    previewWindow.focus();
    cleanup();
  };

  window.addEventListener('message', handleReadyMessage);
  timeoutId = window.setTimeout(() => {
    cleanup();
  }, PREVIEW_TIMEOUT_MS);
};

export default openAnnouncementWebPreview;
